// src\app\components\NumerableTable.jsx
import React from "react"

import { dobToDate, formatDate, getMonYear, isOSX, isSafari, nundefined } from "../modules/utilities"
import { COLOR, COLUMN, COUNTRY, FORMATTING, JARGON, KEY_VALUES, CATEGORY, SCROLLBAR } from "../modules/constants"
import { validateTableInfoPage } from "../pages/companiesHouse/pageFunctions/commonFunctions"

// mui
import { withStyles } from "@mui/styles"
import Table from "@mui/material/Table"
import TableRow from "@mui/material/TableRow"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableFooter from "@mui/material/TableFooter"
import TableContainer from "@mui/material/TableContainer"
import TableSortLabel from '@mui/material/TableSortLabel'
import IconButton from "@mui/material/IconButton"
import Scrollbars from "react-custom-scrollbars-2"
import Tooltip from '@mui/material/Tooltip'

import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded"
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded"

// mui
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined"
import StarIcon from "@mui/icons-material/Star"

const { asc, black, border1, center, country, date, desc, dob, dobMonYear, left, local, none, normal, string, top } = FORMATTING

//#region styles

// Explicit values shared by header and footer cell text so the two are guaranteed
// to render identically, rather than relying on MUI's default per-variant styling.
const tableCellText = {
  color: black,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1,
}

const styles = theme => ({
  container: {
    height: "100%",
    borderLeft: border1,
  },
  table: {
    minWidth: 300,
  },
  headerRow: {
    height: KEY_VALUES.TABLE_ROW_HEIGHT,
  },
  headerCell: {
    ...tableCellText,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  tableRow: {
    cursor: "pointer",
    height: KEY_VALUES.TABLE_ROW_HEIGHT,
  },
  footerRow: {
    cursor: "default",
    height: KEY_VALUES.TABLE_ROW_HEIGHT,
  },
  footerCell: {
    ...tableCellText,
    borderBottom: "0px solid black",
    height: KEY_VALUES.TABLE_ROW_HEIGHT,
    position: "sticky",
  },
  sortIcon: {
    fill: black,
  },
  flagIcon: {
    border: border1,
    height: 20,
    width: 32,
  },
})
//#endregion styles

const processValue = ({ type, value }, classes) => {
  return Array.isArray(value)
    ? value.map((value, index) => {
      return (
        <span
          key={index}>
          {formatValue({ type, value, classes })}
          <br />
        </span>)
    })
    : formatValue({ type, value, classes })
}

const formatValue = ({ type, value, classes }) => {
  switch (type) {

    case country:
      return getFlag(value, classes)

    case date:
      return formatDate(value, false)

    case dob: // date of birth eg
      return formatDob(value) // eg 03/1961

    case dobMonYear: // date of birth
      return value
        ? getMonYear(dobToDate(value)) // eg Feb 1961
        : "N/A"

    case local:
      return nundefined(value) ? JARGON.UNKNOWN : value.toLocaleString()

    case string:
    default:
      return value
  }
}

const getFlag = (country, classes) => {
  return (<img
    alt={country}
    className={classes.flagIcon}
    src={COUNTRY[country].flag}
  />)
}

const formatDob = (value) => {

  if (!value) // No DOB if PSC is a company
    return "N/A"
  else {
    const { day, month, year } = value
    let outString = day

      ? `${day + 100}/`.slice(-(3))
      : ``

    outString += month
      ? `${month + 100}/`.slice(-(3))
      : `--/`

    outString += year
      ? `${year}`
      : `----`

    return outString
  }
}

const rowIcon = (row, handleOnClick) => {

  return (
    /* <Tooltip title={row.cellWithTooltips[0].tooltip} placement={top} disableFocusListener> */
    <TableCell
      align={left}
      onClick={() => handleOnClick(row)}
    >
      {row.icon === CATEGORY.COMPANY && <BusinessRoundedIcon style={{ color: row.iconColor }} />}
      {row.icon === CATEGORY.PERSON && <PersonOutlineRoundedIcon style={{ color: row.iconColor }} />}
    </TableCell>
    /* </Tooltip> */
  )
}

const rowLogo = (row, handleOnClick) => {
  // console.log("logo: " + row.logo)
  return (
    <Tooltip title={row.cellWithTooltips[0].tooltip} placement={top} disableFocusListener>
      <TableCell
        align={center}
        onClick={() => handleOnClick(row)}
        style={{
          paddingLeft: row.paddingLeft,
          paddingRight: row.paddingRight,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        {row.logo === "no logo"
          ? ""
          : <img src={row.logo} alt="logo" style={{ maxHeight: 34, maxWidth: 60, padding: 0, marginTop: 10 }} />
        }
      </TableCell>
    </Tooltip>
  )
}

const favouriteIcon = ({ favouritesEnabled, handleClickFavouriteIcon, kind, row, the350club }) => {
  const isFavourite = !!row?.isFavourite
  const validRowLink = row?.link // only allow favouriting if there is a link - eg '/company/08552252'
  const title = favouritesEnabled
    ? isFavourite ? "click to remove from favourites" : "click to add to favourites"
    : "add/remove favourite - only available when user is logged in"

  return (
    <TableCell align={"left"} style={{ padding: 0 }} >
      {/* // NB: only show tooltip if there is a link to entity page  (ie company number) and therefore the possibility to favourite */}
      <Tooltip placement={"top"} title={title} disableHoverListener={!validRowLink} >
        <IconButton
          // style={{ padding: "10px" }} // affects min row height
          onClick={() => favouritesEnabled && validRowLink && handleClickFavouriteIcon({ row, kind })}
        >
          {favouritesEnabled && validRowLink
            ? isFavourite
              ? <StarIcon style={{ color: COLOR.BRAND.STD }} />
              : <StarBorderOutlinedIcon style={{ color: COLOR.BRAND.STD }} />
            : <StarBorderOutlinedIcon className="starDisabled" />
          }
        </IconButton>
      </Tooltip>
    </TableCell>
  )
}

const mapCellsWithTooltips = (row, handleOnClick, classes) => {
  return row.cellWithTooltips.map((cell, index) => {
    const content = processValue(cell, classes)
    const cellContent = cell.link ? (
      <a
        href={cell.link}
        target={cell.newTab ? "_blank" : "_self"}
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()} // prevent row click
        // If cell color is not set or is black, inherit color from cell. Otherwise, use default link color.
        style={{
          color: cell.color && cell.color !== 'black' ? 'inherit' : undefined
        }}
      >
        {content}
      </a>
    ) : (
      content
    )

    return (
      <Tooltip
        key={index}
        title={cell.tooltip ?? ""}
        placement="top"
        disableHoverListener={!cell?.tooltip}
      >
        <TableCell
          align={cell.align}
          colSpan={cell.colSpan}
          component="th"
          onClick={() => handleOnClick(row)}
          scope="row"
          style={{
            backgroundColor: cell.backgroundColor,
            color: cell.color,
            borderLeft: cell.borderLeft,
            fontWeight: row.fontWeight,
            paddingLeft: cell.paddingLeft,
            paddingRight: cell.paddingRight,
          }}
        >
          {cellContent}
        </TableCell>
      </Tooltip>
    )
  })
}

const getValue = (a, b, index) => (a.cellWithTooltips[index].sortValue ||
  a.cellWithTooltips[index].sortValue === 0)
  ? {
    a: a.cellWithTooltips[index].sortValue,
    b: b.cellWithTooltips[index].sortValue
  }
  : {
    a: a.cellWithTooltips[index].value,
    b: b.cellWithTooltips[index].value
  }

export const sortRows = props => {
  // console.log('sortRows props:', props)
  const { headers, rows, tableOrder, the350club } = props
  const { orderBy, order, dontSort } = tableOrder

  if (!dontSort) {
    // Find 1st column with header.value ie. not icon column
    const offset = headers.findIndex(header => header.label !== "")

    // If no sorting (ie companies house search) orderBy
    // will be undefined and we set index to -1
    var index = orderBy
      ? headers.findIndex(x => x.id === orderBy) - offset
      : -1

    // Hack to handle LSE People case where header has non-sortable "Roles:"
    // column whereas, in rows, the name column has a spans of 2 (1 if 350club as there is no star icon)
    const rolesColumn = the350club ? 1 : 2

    if (headers[rolesColumn]?.id === COLUMN.SKIP_ME && index >= rolesColumn)
      index--
    // If not sorting by 1st labeled colum, then sort by this first
    // Eg sort by 'name' before 'index'
    if (index > 0)
      sortRow(0, asc, rows)

    // Check that index is not -1 (no sorting) and the array not length 0 before sorting
    if (index >= 0 && index < rows[0].cellWithTooltips.length)
      sortRow(index, order, rows)
  }

  return rows
}

// const sortRow = (index, order, rows) => {
//   rows.sort((a, b) => { // sort upwards
//     let values = getValue(a, b, index)

//     return values.a === values.b
//       ? 1
//       : (values.a < values.b)
//         ? order === desc ? 1 : -1
//         : order === desc ? -1 : 1
//   })
// }

const sortRow = (index, order, rows) => {
  rows.sort((a, b) => {
    let values = getValue(a, b, index)

    const aVal = values.a ?? ""
    const bVal = values.b ?? ""

    const isEmptyA = aVal === ""
    const isEmptyB = bVal === ""

    // Always push empty strings to the bottom
    if (isEmptyA && !isEmptyB) return 1
    if (!isEmptyA && isEmptyB) return -1
    if (isEmptyA && isEmptyB) return 0

    // Case-insensitive only for strings
    const aCompare = typeof aVal === "string" ? aVal.toUpperCase() : aVal
    const bCompare = typeof bVal === "string" ? bVal.toUpperCase() : bVal

    if (aCompare === bCompare) return 0

    return (aCompare < bCompare)
      ? (order === desc ? 1 : -1)
      : (order === desc ? -1 : 1)
  })
}

export const sliceRows = (rows, tableInfo) => {
  tableInfo = validateTableInfoPage({ ...tableInfo, count: rows.length })
  let start = tableInfo.rowsPerPage * tableInfo.page
  const end = tableInfo.rowsPerPage * (tableInfo.page + 1)
  return rows.slice(start, end)
}

// Returns "" if orderBy, headCell.label or headCell.sortLabel are false.
// Otherwise, the supplied tooltipTitle or a generated title if tooltipTitle is undefined
const getTooltipTitle = ({ headCell, orderBy }) =>
  orderBy && headCell.label && headCell.sortLabel
    ? headCell.tooltipTitle ?? `click to sort by ${headCell.label}`
    : ""

const StickyTableHeader = props => {
  // console.log('StickyTableHeader props:',props)

  const { classes, headers, onRequestSort, orderBy, order, headerStyle, backgroundColor } = props

  const createSortHandler = orderBy => (event) => {
    onRequestSort(event, orderBy)
  }

  return (
    <TableHead>
      <TableRow className={classes.headerRow} >
        {headers.map(headCell => (
          <Tooltip
            key={headCell.id}
            title={getTooltipTitle({ headCell, orderBy })}
            placement={top}
          >
            <TableCell
              align={headCell.align}
              className={classes.headerCell}
              colSpan={headCell.colSpan}
              key={headCell.id}
              // padding={headCell.disablePadding ? none : normal}
              style={{
                backgroundColor,
                borderLeft: headCell.borderLeft,
                paddingLeft: headCell.paddingLeft,
                paddingRight: headCell.paddingRight,
                width: headCell.width,
              }}
            >
              {/* add TableSortLabel if orderBy and label are 'true' */}
              {orderBy && headCell.label && headCell.sortLabel && <TableSortLabel
                active={orderBy === headCell.id}
                classes={{ icon: headerStyle === "favourites" ? classes.sortIcon : null }}
                direction={orderBy === headCell.id ? order : asc}
                onClick={createSortHandler(headCell.id)}
                style={{ color: black, lineHeight: 1 }}
              >
                {headCell.label}
              </TableSortLabel>}
              {/* add standard label if orderBy or headCell.sortLabel are 'false' */}
              {(!orderBy || !headCell.sortLabel) && headCell.label}
            </TableCell>
          </Tooltip>
        ))}
      </TableRow>
    </TableHead>
  )
}

const StickyTableFooter = props => {
  // console.log('StickyTableFooter props:',props)

  const { footers, portrait } = props
  // console.log("footers: ", footers)
  // console.log("OSX: " + isOSX() + ", isSafari: " + isSafari())

  const tBottom = portrait || footers.means == null /* || isIos() */
    ? "-1px"
    : `${KEY_VALUES.TABLE_ROW_HEIGHT - 1}px`

  const mBottom = /* isIos() || */ (isOSX() && isSafari())
    ? `-${KEY_VALUES.TABLE_ROW_HEIGHT + 1}px` // was + 1
    : "-1px"

  const borderBottom = border1
  const showMeans = !portrait && footers.means != null

  return (
    <TableFooter>
      {TableFooterRow({ ...props, borderBottom: showMeans ? 0 : borderBottom, bottom: tBottom, footers: footers.totals })}

      {/* bottom footer row - averages - only show in landscape format [and not iOS] */}
      {showMeans && TableFooterRow({ ...props, bottom: mBottom, borderBottom, footers: footers.means })}
    </TableFooter>
  )
}

const TableFooterRow = ({ backgroundColor, bottom, borderBottom, classes, footers }) => {
  return (
    <TableRow className={classes.footerRow}>
      {footers.map(footCell => (
        <Tooltip
          key={footCell.id}
          title={footCell.tooltipTitle ?? ""}
          placement={"top"}
        >
          <TableCell className={classes.footerCell}
            align={footCell.align}
            colSpan={footCell.colSpan}
            key={footCell.id}
            // padding={footCell.disablePadding ? none : normal}
            style={{
              backgroundColor,
              borderBottom,
              borderLeft: footCell.borderLeft,
              bottom,
              paddingLeft: footCell.paddingLeft,
              paddingRight: footCell.paddingRight,
              // set inline (rather than relying on the footerCell class) so these
              // always win over MUI's default TableCell "footer" variant styling
              // (which otherwise renders footer text smaller and grey)
              ...tableCellText,
            }}
          >
            {footCell.label}
          </TableCell>
        </Tooltip>
      ))}
    </TableRow>
  )
}

const NumerableTableContent = props => {
  // console.log('NumerableTableContent props:', props)

  const { footers, handleOnClick, handleOrderTable, headers, headerStyle, portrait, rows, tableInfo, the350club } = props.table
  // tableOrder will be undefined/null if the table is not sortable
  const tableOrder = props.table.tableOrder || {}
  const classes = props.classes
  const handleRequestSort = (event, orderBy) => {
    const isAsc = tableOrder.orderBy === orderBy && tableOrder.order === asc
    const order = isAsc ? desc : asc
    handleOrderTable({ orderBy, order })
  }

  const sortedRows = rows?.length > 0
    ? sortRows({ headers, rows, tableOrder, tableInfo, the350club })
    : []

  const backgroundColor = COLOR.BRAND.LIGHT

  return (
    <Table stickyHeader className={classes.table} aria-label="sortable table" size="small" >
      <StickyTableHeader
        backgroundColor={backgroundColor}
        classes={classes}
        headers={headers}
        headerStyle={headerStyle}
        onRequestSort={handleRequestSort}
        order={tableOrder.order || asc}
        orderBy={tableOrder.orderBy}
      />

      <TableBody>
        {sliceRows(sortedRows, tableInfo)
          .map((row, index) => (
            <TableRow
              className={classes.tableRow}
              key={index}
              id="table-row"
              hover={!!row.link}
            >
              {/* No favourite column for LSE Roles so using the isFavourite props to filter this */}
              {/* NB: != null is the same as !== "undefined" or !== null */}
              {row.isFavourite != null && !the350club && favouriteIcon({ ...props.table, row })}
              {row.icon && rowIcon(row, handleOnClick)}
              {row.logo && rowLogo(row, handleOnClick)}
              {mapCellsWithTooltips(row, handleOnClick, classes)}
            </TableRow>
          ))}
      </TableBody>

      {footers && footers.totals && footers.totals.length > 0 && <StickyTableFooter
        backgroundColor={backgroundColor}
        classes={classes}
        footers={footers}
        portrait={portrait}
      // onRequestSort={handleRequestSort}
      // order={tableOrder.order}
      // orderBy={tableOrder.orderBy}
      // headerStyle={headerStyle}
      />}

    </Table>
  )
}

const NumerableTable = props => {
  // console.log('NumerableTable props:',props)

  const { portrait } = props.table
  const classes = props.classes

  return (
    <React.Fragment>
      <TableContainer className={classes.container} >

        {!portrait && <Scrollbars autoHide
          autoHideTimeout={SCROLLBAR.AH_TIMEOUT}
          autoHideDuration={SCROLLBAR.AH_DURATION}>
          {NumerableTableContent(props)}
        </Scrollbars>}

        {portrait && NumerableTableContent(props)}

      </TableContainer>
    </React.Fragment >
  )
}

export default withStyles(styles)(NumerableTable)
